import ApiService from "../Services/ApiService";

export interface TransportSubscriptionInfo {
    id: number;
    type: "navigo" | "ter" | "other";
    typeLabel: string;
    validFrom: string;
    validUntil: string;
    /** Client du couple prof/client ; null pour un abonnement « tous clients » (ancien dépôt). */
    clientId?: number | null;
    clientName?: string | null;
    isExpired: boolean;
    /** Signé : négatif quand l'abonnement est déjà expiré. Toujours tester isExpired d'abord. */
    expiresInDays: number;
    url: string;
    creationDatetime: string | null;
    /** À confirmer après un changement d'adresse (prof ou client) : ne vaut pas preuve. */
    needsConfirmation?: boolean;
    confirmationReason?: string | null;
    confirmedAt?: string | null;
}

export const TRANSPORT_SUBSCRIPTION_TYPES: Array<{ value: TransportSubscriptionInfo["type"]; label: string }> = [
    { value: "navigo", label: "Navigo" },
    { value: "ter", label: "TER" },
    { value: "other", label: "Autre abonnement" },
];

/**
 * P6 : justificatifs d'abonnement de transports en commun du prof, rattachés
 * à un client (trajet du domicile du prof vers celui de ce client). Un
 * abonnement valide sert automatiquement de preuve de déplacement pour les
 * déclarations de ce client couvertes par sa période de validité.
 */
export default abstract class TransportSubscriptionRepository {
    /** Dépôt d'un justificatif (fichier + type + dates de validité). */
    public static create(
        teachrId: number,
        file: File,
        type: string,
        validFrom: string,
        validUntil: string,
        clientId?: number | null
    ): Promise<TransportSubscriptionInfo> {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);
        formData.append("validFrom", validFrom);
        formData.append("validUntil", validUntil);
        if (clientId != null) formData.append("clientId", String(clientId));
        return ApiService.postInstance(`/teachrs/${teachrId}/transport-subscriptions`, null, formData, false, true);
    }

    /** Avec clientId : abonnements valables pour ce client et abonnement courant du couple. */
    public static list(teachrId: number, clientId?: number | null): Promise<{ current: TransportSubscriptionInfo | null; subscriptions: TransportSubscriptionInfo[] }> {
        const query = clientId != null ? `?client=${clientId}` : "";
        return ApiService.get(`/teachrs/${teachrId}/transport-subscriptions${query}`);
    }

    /** Le prof confirme que l'abonnement couvre toujours le trajet après un changement d'adresse. */
    public static confirm(subscriptionId: number): Promise<TransportSubscriptionInfo> {
        return ApiService.post(`/transport-subscriptions/${subscriptionId}/confirm`, {}) as Promise<TransportSubscriptionInfo>;
    }

    public static remove(subscriptionId: number): Promise<{ id: number; deleted: boolean }> {
        return ApiService.deleteInstance(`/transport-subscriptions/${subscriptionId}`) as Promise<any>;
    }

    /**
     * Rattache l'abonnement valide du prof comme preuve de déplacement du
     * cours (scénario > 160 km) : le cours passe en attente admin sans upload.
     */
    public static attachToCourse(courseId: number): Promise<{ declarationStatus: string; subscription: TransportSubscriptionInfo }> {
        return ApiService.post(`/punctual-courses/${courseId}/travel-proof/subscription`, {}) as Promise<any>;
    }
}
